import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileSpreadsheet, Cloud, Menu, X, PlayCircle } from "lucide-react";

const LINKS = [["#video", "Vídeo"], ["#como-funciona", "Cómo funciona"], ["#calculadora", "Calculadora"], ["#incluye", "Qué incluye"], ["#contacto", "Contacto"]];

export function Logo({ light = false }) {
  return (
    <img src={light ? "/img/farmanaliza-logo-blanco.png?v=3" : "/img/farmanaliza-logo.png?v=3"} alt="Farmanaliza · Stocks, ventas, equipo" className="h-14 w-auto" />
  );
}

export function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    f(); window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className={"fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 " + (scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_1px_0_#e2e8f0]" : "bg-transparent")} data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="landing-logo"><Logo light={!scrolled} /></a>
        <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold">
          {LINKS.map(([h, l]) => (
            <a key={h} href={h} className={(scrolled ? "text-slate-600 hover:text-[#12305e]" : "text-white/80 hover:text-white") + " transition-colors"} data-testid={`landing-nav-${h.slice(1)}`}>{l}</a>
          ))}
        </nav>
        <div className="hidden sm:flex items-center gap-2">
          <Link to="/login" className={"px-4 py-2 rounded-full text-sm font-semibold transition-colors " + (scrolled ? "text-[#12305e] hover:bg-slate-100" : "text-white hover:bg-white/10")} data-testid="landing-nav-login">Acceder</Link>
          <a href="#contacto" className="px-4 py-2 rounded-full text-sm font-bold bg-[#1f6fd1] text-white hover:bg-[#1658a8] transition-colors" data-testid="landing-nav-cta">Solicitar información</a>
        </div>
        <button className={"sm:hidden p-2 " + (scrolled ? "text-[#12305e]" : "text-white")} onClick={() => setOpen((o) => !o)} data-testid="landing-nav-menu" aria-label="Menú">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="sm:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3" data-testid="landing-nav-mobile">
          {LINKS.map(([h, l]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block font-semibold text-slate-700">{l}</a>)}
          <Link to="/login" className="block font-semibold text-[#1f6fd1]">Acceder a mi cuenta</Link>
        </div>
      )}
    </header>
  );
}

export function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#0b1f3f]" data-testid="landing-hero">
      <img src="/img/farmanaliza-banner.jpg?v=2" alt="Farmacéutica revisando el stock de su farmacia" className="absolute inset-0 w-full h-full object-cover object-[80%_center] origin-[75%_45%] hero-zoom" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f3f] via-[#0b1f3f]/85 to-[#0b1f3f]/10" />
      <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0b1f3f] to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3f] via-transparent to-transparent" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-20 pt-32 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-blue-200 hero-in" style={{ animationDelay: "80ms" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Archiduque Consulting
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] mt-6 hero-in" style={{ animationDelay: "180ms" }}>
            Tu stock parado es dinero.<br /><span className="text-[#5aa2f0]">Recupéralo.</span>
          </h1>
          <p className="text-base md:text-lg text-blue-100/90 mt-6 max-w-xl leading-relaxed hero-in" style={{ animationDelay: "300ms" }}>
            Farmanaliza clasifica tu inventario ABCD, encuentra el stock que no rota, agrupa los sustitutivos de parafarmacia con IA y pone a tu equipo a vender lo que de verdad te conviene.
          </p>
          <div className="flex flex-wrap gap-3 mt-9 hero-in" style={{ animationDelay: "420ms" }}>
            <a href="#contacto" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1f6fd1] text-white font-bold hover:bg-[#1658a8] transition-colors shadow-[0_10px_40px_-10px_#1f6fd1]" data-testid="hero-cta">
              Solicitar información <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#video" className="group inline-flex items-center gap-3 pl-3 pr-7 py-2 rounded-full bg-white text-[#12305e] text-lg font-extrabold hover:bg-blue-50 transition-colors" data-testid="hero-ver-video"
               onClick={(e) => { e.preventDefault(); document.getElementById("video")?.scrollIntoView({ behavior: "smooth" }); setTimeout(() => document.querySelector("[data-testid=landing-video-player]")?.play?.().catch(() => {}), 700); }}>
              <PlayCircle size={40} strokeWidth={1.75} className="text-[#1f6fd1] group-hover:scale-110 transition-transform" /> Ver vídeo
            </a>
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="hero-login">Acceder a mi cuenta</Link>
          </div>
          <ul className="mt-12 grid sm:grid-cols-3 gap-4 text-sm text-blue-100/90 hero-in" style={{ animationDelay: "540ms" }}>
            {[[FileSpreadsheet, "Excel, TXT o CSV de cualquier programa"], [Cloud, "Sin instalar nada"], [ShieldCheck, "Datos de tu farmacia cifrados"]].map(([I, t]) => (
              <li key={t} className="flex items-start gap-2"><I size={16} className="text-emerald-400 mt-0.5 shrink-0" />{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
